import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "../icons";
import { MailOutline as MailOutlineIcon } from "../icons";
import { T } from "./Translate";
import PublicPageLayout, { useAuthUser } from "../components/PublicPageLayout";
import supportFaqData from "./supportFaqData";

// Páginas institucionales públicas (/about, /faq, /contact). Se ven sin
// iniciar sesión y están en el sitemap: le muestran a Google (y a cualquier
// visitante) qué es Notiline y quién está detrás.

const SUPPORT_EMAIL = "support@notiline.com";

const Section = ({ title, children }) => (
  <Box>
    <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
      {title}
    </Typography>
    <Typography component="div" sx={{ color: "text.secondary", lineHeight: 1.7 }}>
      {children}
    </Typography>
  </Box>
);

const FEATURES = [
  {
    title: <T>Ofertas y cupones</T>,
    text: <T>Comercios y marcas publican descuentos, promociones y cupones. Cada oferta muestra el precio original, el descuento y dónde comprar.</T>,
  },
  {
    title: <T>Eventos</T>,
    text: <T>Organizadores publican eventos gratuitos o pagos con fechas, horarios y ubicación. Los usuarios pueden confirmar asistencia y comprar entradas.</T>,
  },
  {
    title: <T>Tiendas</T>,
    text: <T>Cualquier negocio puede abrir su tienda en Notiline, cargar sus productos y vender con pagos a través de PayPal.</T>,
  },
  {
    title: <T>Autos</T>,
    text: <T>Compra y venta de vehículos nuevos, usados o para reparar, con fotos, kilometraje y contacto directo con el vendedor por chat.</T>,
  },
  {
    title: <T>Viajes y propiedades</T>,
    text: <T>Alojamientos, alquileres, propiedades en venta y combos de vuelo más alojamiento publicados por sus dueños y agencias.</T>,
  },
  {
    title: <T>Red de negocios</T>,
    text: <T>Perfiles de empresas, hilos para conversar, seguidores, chat y mapa para encontrar negocios y publicaciones cerca tuyo.</T>,
  },
];

export const About = () => {
  const { checked, user } = useAuthUser();
  return (
    <PublicPageLayout title={<T>Quiénes somos</T>} loading={!checked} isGuest={!user}>
      <Stack spacing={3}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          <T>Qué es Notiline</T>
        </Typography>
        <Typography sx={{ color: "text.secondary", lineHeight: 1.7 }}>
          <T>Notiline es una red de negocios donde empresas, comercios, organizadores y personas publican y descubren ofertas, eventos, tiendas, autos y viajes en un solo lugar. Conectamos a quienes venden u organizan con quienes buscan, y ofrecemos herramientas para comunicarse y concretar compras de forma segura.</T>
        </Typography>

        <Grid container spacing={2}>
          {FEATURES.map((f, i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Box sx={{ p: 2, height: "100%", borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{f.title}</Typography>
                <Typography sx={{ fontSize: 14, color: "text.secondary" }}>{f.text}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Section title={<T>Quién está detrás</T>}>
          <T>Notiline es un producto de Dapur Tecnología. Desarrollamos y operamos la plataforma, revisamos las denuncias de contenido y damos soporte a usuarios y negocios.</T>
        </Section>

        <Section title={<T>Cómo funciona</T>}>
          <T>Navegar es gratis. Para publicar, los usuarios eligen un plan de suscripción según la cantidad de publicaciones que necesitan. Las compras de productos y entradas se pagan a través de PayPal, y las consultas por autos y viajes se hacen por el chat de la app.</T>
        </Section>

        <Section title={<T>Seguridad y confianza</T>}>
          <T>Las cuentas verificadas muestran una insignia, cualquier usuario puede bloquear o denunciar a otro, y el chat de autos y viajes filtra datos de contacto para proteger a compradores y vendedores. Más detalles en nuestros</T>{" "}
          <Link component={RouterLink} to="/terms"><T>Términos y condiciones</T></Link>{" "}
          <T>y nuestra</T>{" "}
          <Link component={RouterLink} to="/privacyPolicy"><T>Política de privacidad</T></Link>.
        </Section>
      </Stack>
    </PublicPageLayout>
  );
};

export const Faq = () => {
  const { checked, user } = useAuthUser();
  const [expanded, setExpanded] = useState(null);
  return (
    <PublicPageLayout title={<T>Preguntas frecuentes</T>} loading={!checked} isGuest={!user}>
      <Stack spacing={3}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          <T>Preguntas frecuentes</T>
        </Typography>
        {supportFaqData.map((category) => (
          <Box key={category.id}>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              {category.icon} {category.label}
            </Typography>
            {category.questions.map((item, i) => {
              const id = `${category.id}-${i}`;
              return (
                <Accordion
                  key={id}
                  disableGutters
                  expanded={expanded === id}
                  onChange={(_, open) => setExpanded(open ? id : null)}
                  sx={{ bgcolor: "transparent", boxShadow: "none", borderBottom: "1px solid", borderColor: "divider", "&:before": { display: "none" } }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 600 }}>{item.q}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography sx={{ color: "text.secondary", lineHeight: 1.7 }}>{item.a}</Typography>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        ))}
        <Typography sx={{ color: "text.secondary" }}>
          <T>¿No encontraste tu respuesta?</T>{" "}
          <Link component={RouterLink} to="/contact"><T>Escribinos</T></Link>.
        </Typography>
      </Stack>
    </PublicPageLayout>
  );
};

export const Contact = () => {
  const { checked, user } = useAuthUser();
  return (
    <PublicPageLayout title={<T>Contacto</T>} loading={!checked} isGuest={!user}>
      <Stack spacing={3}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          <T>Contacto</T>
        </Typography>
        <Typography sx={{ color: "text.secondary", lineHeight: 1.7 }}>
          <T>¿Tenés una consulta, un problema con tu cuenta, una propuesta comercial o querés denunciar una publicación? Escribinos y te respondemos a la brevedad.</T>
        </Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<MailOutlineIcon />}
            href={`mailto:${SUPPORT_EMAIL}`}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {SUPPORT_EMAIL}
          </Button>
        </Box>
        <Section title={<T>Antes de escribirnos</T>}>
          <T>Muchas dudas sobre cuentas, suscripciones, pagos y publicaciones ya están respondidas en las</T>{" "}
          <Link component={RouterLink} to="/faq"><T>Preguntas frecuentes</T></Link>.{" "}
          <T>Si ya tenés cuenta, también podés usar el Centro de Soporte dentro de la app.</T>
        </Section>
        <Section title={<T>Reembolsos</T>}>
          <T>Las condiciones de reembolso de suscripciones y compras están en nuestra</T>{" "}
          <Link component={RouterLink} to="/refunds"><T>Política de reembolsos</T></Link>.
        </Section>
      </Stack>
    </PublicPageLayout>
  );
};
